import { useQuery, UseQueryResult } from 'react-query';
import { EventHasCalendarConflict, UrlParams } from 'types';
import { client } from 'utils/api-client';

/**
 * Single-session staff calendar-conflict check (coach availability + Google Calendar).
 *
 * Caches the unwrapped response payload, not the AxiosResponse, so it shares cache
 * entries safely with `useCheckSessionsCalendarConflicts` — the bulk hook's first row
 * derives the same start/end/staffs as the single-session anchor and therefore hashes
 * to the same query key.
 */
function useCheckCalendarConflicts({
  startDate,
  endDate,
  staffs,
  enabled = true,
}: {
  startDate: string | null;
  endDate: string | null;
  staffs: string[] | null;
  enabled?: boolean;
}): UseQueryResult<EventHasCalendarConflict> {
  const queryFn = async (): Promise<EventHasCalendarConflict> => {
    const params: UrlParams = {};

    if (staffs && !!staffs.length) {
      params.staffs = staffs.join(',');
    }
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }

    const response = await client.get<EventHasCalendarConflict>(`events/check-calendar-conflicts/`, {
      params,
    });

    return response.data;
  };

  return useQuery(['events', 'check-calendar-conflicts', startDate, endDate, staffs], queryFn, {
    enabled: enabled && !!staffs && !!staffs.length && !!startDate && !!endDate && startDate !== endDate,
    staleTime: 30 * 1000, // Consider results fresh for 30 seconds
    cacheTime: 60 * 1000, // Keep results in cache for 1 minute
  });
}

export default useCheckCalendarConflicts;
